import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { groupFinanceApi } from "@/features/group_finance/api/groupFinance";

const K = {
  latestBalances: ["gf-latest-balances"] as const,
  balances: (company?: string) => ["gf-balances", company] as const,
  plannedEntries: (type?: string) => ["gf-planned", type] as const,
  assets: ["gf-assets"] as const,
  vat: (type?: string) => ["gf-vat", type] as const,
  vatOverpaid: ["gf-vat-overpaid"] as const,
  cashflow: ["gf-cashflow"] as const,
  balanceHistory: (company?: string) => ["gf-balance-history", company] as const,
};

export function useCompanies(enabled = true) {
  return useQuery({ queryKey: ["gf-companies"], queryFn: groupFinanceApi.getCompanies, enabled });
}

export function useCompanyChoices() {
  return useQuery({ queryKey: ["gf-company-choices"], queryFn: groupFinanceApi.getCompanyChoices });
}

export function useLatestBalances() {
  return useQuery({ queryKey: K.latestBalances, queryFn: groupFinanceApi.getLatestBalances });
}

export function useCompanyBalances(companyId?: string) {
  return useQuery({
    queryKey: K.balances(companyId),
    queryFn: () => groupFinanceApi.getBalances(companyId),
  });
}

export function useCreateBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createBalance,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gf-balances"] });
      qc.invalidateQueries({ queryKey: K.latestBalances });
      qc.invalidateQueries({ queryKey: ["gf-balance-history"] });
    },
  });
}

export function useUpdateBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateBalance>[1] }) =>
      groupFinanceApi.updateBalance(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gf-balances"] });
      qc.invalidateQueries({ queryKey: K.latestBalances });
      qc.invalidateQueries({ queryKey: ["gf-balance-history"] });
    },
  });
}

export function useDeleteBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteBalance,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gf-balances"] });
      qc.invalidateQueries({ queryKey: K.latestBalances });
    },
  });
}

export function usePlannedEntries(type?: "cost" | "revenue") {
  return useQuery({
    queryKey: K.plannedEntries(type),
    queryFn: () => groupFinanceApi.getPlannedEntries(type),
  });
}

export function useCreatePlannedEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createPlannedEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-planned"] }),
  });
}

export function useUpdatePlannedEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updatePlannedEntry>[1] }) =>
      groupFinanceApi.updatePlannedEntry(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-planned"] }),
  });
}

export function useDeletePlannedEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deletePlannedEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-planned"] }),
  });
}

export function useNonLiquidAssets() {
  return useQuery({ queryKey: K.assets, queryFn: groupFinanceApi.getAssets });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createAsset,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.assets }),
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateAsset>[1] }) =>
      groupFinanceApi.updateAsset(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.assets }),
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteAsset,
    onSuccess: () => qc.invalidateQueries({ queryKey: K.assets }),
  });
}

export function useVATEntries(type?: string) {
  return useQuery({
    queryKey: K.vat(type),
    queryFn: () => groupFinanceApi.getVATEntries(type),
  });
}

export function useCreateVATEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createVATEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-vat"] }),
  });
}

export function useUpdateVATEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateVATEntry>[1] }) =>
      groupFinanceApi.updateVATEntry(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-vat"] }),
  });
}

export function useDeleteVATEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteVATEntry,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-vat"] }),
  });
}

export function useVATOverpaid() {
  return useQuery({ queryKey: K.vatOverpaid, queryFn: groupFinanceApi.getVATOverpaid });
}

export function useVATCollected() {
  return useQuery({ queryKey: ["gf-vat-collected"], queryFn: groupFinanceApi.getVATCollected });
}

export function useGroupCashflow(companies?: string[]) {
  const companiesKey = companies === undefined ? "__all__" : [...companies].sort().join(",");
  return useQuery({
    queryKey: [...K.cashflow, companiesKey],
    queryFn: () => groupFinanceApi.getCashflow(companies),
    enabled: companies === undefined || companies.length > 0,
    placeholderData: [],
  });
}

export function useIntercompanyRecurring() {
  return useQuery({ queryKey: ["gf-ic-recurring"], queryFn: groupFinanceApi.getIntercompanyRecurring });
}

export function useCreateIntercompanyRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createIntercompanyRecurring,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-recurring"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useUpdateIntercompanyRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateIntercompanyRecurring>[1] }) =>
      groupFinanceApi.updateIntercompanyRecurring(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-recurring"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useDeleteIntercompanyRecurring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteIntercompanyRecurring,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-recurring"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useIntercompanyOneTime() {
  return useQuery({ queryKey: ["gf-ic-onetime"], queryFn: groupFinanceApi.getIntercompanyOneTime });
}

export function useCreateIntercompanyOneTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createIntercompanyOneTime,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-onetime"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useUpdateIntercompanyOneTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateIntercompanyOneTime>[1] }) =>
      groupFinanceApi.updateIntercompanyOneTime(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-onetime"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useDeleteIntercompanyOneTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteIntercompanyOneTime,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gf-ic-onetime"] }); qc.invalidateQueries({ queryKey: ["gf-cashflow"] }); },
  });
}

export function useVATSuggestion(period: string | null, company?: string | null) {
  return useQuery({
    queryKey: ["gf-vat-suggestion", period, company],
    queryFn: () => groupFinanceApi.getVATSuggestion(period!, company),
    enabled: !!period,
  });
}

export function useCITSuggestion(period: string | null) {
  return useQuery({
    queryKey: ["gf-cit-suggestion", period],
    queryFn: () => groupFinanceApi.getCITSuggestion(period!),
    enabled: !!period,
  });
}

export function useMarkVATEntryPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_paid, paid_date }: { id: string; is_paid: boolean; paid_date?: string | null }) =>
      groupFinanceApi.updateVATEntry(id, { is_paid, paid_date }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["gf-vat"] });
      qc.invalidateQueries({ queryKey: ["gf-cashflow"] });
    },
  });
}

export function useBalanceHistory(companyId?: string) {
  return useQuery({
    queryKey: K.balanceHistory(companyId),
    queryFn: () => groupFinanceApi.getBalanceHistory(companyId),
  });
}

export function useMonthlyPrzRevenue(companies?: string[]) {
  const companiesKey =
    companies === undefined ? "__all__" : [...companies].sort().join(",");
  return useQuery({
    queryKey: ["gf-monthly-prz-revenue", companiesKey],
    queryFn: () => groupFinanceApi.getMonthlyPrzRevenue(companies),
    enabled: companies === undefined || companies.length > 0,
    placeholderData: { issued: {}, drafts: {} },
  });
}

export function useMonthlyExtraCosts() {
  return useQuery({
    queryKey: ["gf-monthly-extra-costs"],
    queryFn: groupFinanceApi.getMonthlyExtraCosts,
  });
}

export function useSetMonthlyExtraCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ yearMonth, amount }: { yearMonth: string; amount: string }) =>
      groupFinanceApi.setMonthlyExtraCost(yearMonth, amount),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-monthly-extra-costs"] }),
  });
}

export function useOutstandingReceivables() {
  return useQuery({
    queryKey: ["gf-outstanding-receivables"],
    queryFn: groupFinanceApi.getOutstandingReceivables,
  });
}

export function useExchangeRates() {
  return useQuery({
    queryKey: ["gf-exchange-rates"],
    queryFn: groupFinanceApi.getExchangeRates,
    staleTime: 1000 * 60 * 60, // 1h — rates change once per business day
  });
}

export function useCurrencyAccounts(companyId?: string) {
  return useQuery({
    queryKey: ["gf-currency-accounts", companyId],
    queryFn: () => groupFinanceApi.getCurrencyAccounts(companyId),
  });
}

export function useCreateCurrencyAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.createCurrencyAccount,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-currency-accounts"] }),
  });
}

export function useUpdateCurrencyAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof groupFinanceApi.updateCurrencyAccount>[1] }) =>
      groupFinanceApi.updateCurrencyAccount(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-currency-accounts"] }),
  });
}

export function useDeleteCurrencyAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: groupFinanceApi.deleteCurrencyAccount,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["gf-currency-accounts"] }),
  });
}
